/**
 * CRE PACSShare tab — Search → Review → Share Link, then reuse Twilio Invite Template.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PacsSharePanel from "./PacsSharePanel";
import PacsShareChatCard from "./PacsShareChatCard";
import InviteTemplateModal from "./InviteTemplateModal";

function PacsShareTab({
  chatConnection = {},
  studyId = "",
  language = "en",
  studyData = null,
  organisationId = "",
} = {}) {
  const { t } = useTranslation("common");
  const sendInvite = chatConnection.sendInvite;
  const hasConversation = chatConnection.hasConversation || false;
  const connectedNumber = chatConnection.connectedNumber || "";
  const connectWithNumber = chatConnection.connectWithNumber;
  const changeNumber = chatConnection.changeNumber;
  const status = chatConnection.status || "idle";
  const errorMessage = chatConnection.errorMessage || "";

  const [pacsDownloadUrl, setPacsDownloadUrl] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [manualNumber, setManualNumber] = useState("");
  const [numberError, setNumberError] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    const referringPhysicianNumber = studyData?.insuranceNumber || "";
    if (
      referringPhysicianNumber &&
      !manualNumber &&
      (status === "awaitingNumber" || status === "idle")
    ) {
      setManualNumber(referringPhysicianNumber);
    }
  }, [studyData, status, manualNumber]);

  useEffect(() => {
    if (status === "ready" || status === "error" || status === "awaitingNumber") {
      setIsConnecting(false);
    }
    if (status === "awaitingNumber") {
      setNumberError("");
    }
  }, [status, studyData]);

  const isValidPhoneNumber = (phone) => {
    const digitsOnly = String(phone || "").replace(/\D/g, "");
    return digitsOnly.length >= 7 && digitsOnly.length <= 15;
  };

  const handleChangeNumber = useCallback(() => {
    if (!changeNumber) return;
    setManualNumber(connectedNumber || "");
    setNumberError("");
    changeNumber();
    toast.info(t("chatNumberChanged"));
  }, [changeNumber, connectedNumber, t]);

  const handleConnect = useCallback(() => {
    const number = manualNumber.trim();
    if (!number) {
      setNumberError(t("chatEnterNumber") || "Please enter a WhatsApp number");
      return;
    }
    if (!isValidPhoneNumber(number)) {
      setNumberError(
        t("chatInvalidPhoneNumber") ||
          "Invalid phone number. Must be 7-15 digits.",
      );
      return;
    }
    setNumberError("");
    setIsConnecting(true);
    connectWithNumber?.(number);
  }, [manualNumber, connectWithNumber, t]);

  const handleSendInvite = useCallback(
    async (data) => {
      if (!sendInvite) return;
      await sendInvite(data);
    },
    [sendInvite],
  );

  const handleUseInInvite = useCallback(() => {
    if (!sendInvite || !hasConversation) {
      toast.info(
        t("pacsShareConnectChatFirst") ||
          "Connect WhatsApp first to send the template.",
      );
      return;
    }
    setShowInviteModal(true);
  }, [sendInvite, hasConversation, t]);

  return (
    <div className="mt-6 dark:bg-gray-900">
      <PacsSharePanel
        organisationId={organisationId}
        accessionNumber={studyData?.accessionNo || ""}
        onShareLinkReady={setPacsDownloadUrl}
        phoneNumber={manualNumber}
        onPhoneNumberChange={(value) => {
          setManualNumber(value);
          setNumberError("");
        }}
        onConnectPhone={handleConnect}
        isConnectingPhone={isConnecting || status === "loading"}
        phoneError={
          numberError ||
          (status === "awaitingNumber" && errorMessage
            ? t(errorMessage) || errorMessage
            : "")
        }
        connectedNumber={connectedNumber}
        onPhoneKeyDown={(e) => {
          if (e.key === "Enter" && !isConnecting) {
            handleConnect();
          }
        }}
        onChangeNumber={handleChangeNumber}
        chatCard={
          <PacsShareChatCard
            chatConnection={chatConnection}
            language={language}
            shareLinkReady={Boolean(pacsDownloadUrl)}
            onSendTemplate={handleUseInInvite}
            canSendInvite={Boolean(hasConversation && sendInvite)}
          />
        }
      />
      <InviteTemplateModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSend={handleSendInvite}
        language={language}
        defaultParam1={studyData?.insuranceName || ""}
        defaultParam2={""}
        defaultParam3={pacsDownloadUrl || ""}
        studyId={studyId}
        accessionNo={studyData?.accessionNo}
      />
    </div>
  );
}

export default PacsShareTab;
